import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  AiOutlineSearch,
  AiOutlineEye,
  AiOutlineCheckCircle,
  AiOutlineCloseCircle,
  AiOutlineFileExcel,
} from "react-icons/ai";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  FormGroup,
  Input,
  Button,
  Badge,
  Label,
  Table,
} from "reactstrap";
import * as XLSX from "xlsx";
import "../../../styles/dashboard/SuperAdmin/OrganizationManagement.css";
import "../../../styles/dashboard/SuperAdmin/RegistrationRequests.css";
import { formatTableDateTime12H } from "../../../utils/dateFormatter";
import Spinner from "../../common/Spinner";

const API_URL = process.env.REACT_APP_API_URL;
const getToken = () => sessionStorage.getItem("token") || localStorage.getItem("token");

export default function RegistrationRequests() {
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const requestsPerPage = 10;

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [newStatus, setNewStatus] = useState("");

  // Fetch requests from backend
  const fetchRequests = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const response = await axios.get(`${API_URL}/contact/requests`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          page: currentPage,
          limit: requestsPerPage,
          status: filterStatus !== "all" ? filterStatus : undefined,
        },
      });

      if (response.data.success) {
        setRequests(response.data.data);
        setTotalPages(response.data.pagination.totalPages);
        setTotalRecords(response.data.pagination.total);
      }
    } catch (error) {
      console.error("Error fetching registration requests:", error);
      showToast("Failed to load registration requests", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [currentPage, filterStatus]);

  // Apply client-side filters
  useEffect(() => {
    let filtered = [...requests];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (req) =>
          req.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          req.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          req.organizationName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          req.state?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          req.mobile?.includes(searchTerm) ||
          req.referralAgentCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          req.referralAgentName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Date range filter
    if (dateFrom || dateTo) {
      filtered = filtered.filter((req) => {
        const reqDate = new Date(req.createdAt);
        const from = dateFrom ? new Date(dateFrom) : null;
        const to = dateTo ? new Date(dateTo) : null;

        if (from && to) {
          return reqDate >= from && reqDate <= to;
        } else if (from) {
          return reqDate >= from;
        } else if (to) {
          return reqDate <= to;
        }
        return true;
      });
    }

    setFilteredRequests(filtered);
  }, [requests, searchTerm, dateFrom, dateTo]);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 3000);
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setNewStatus(request.status);
    setModalOpen(true);
  };

  const handleStatusUpdate = async () => {
    if (!selectedRequest || !newStatus) return;

    setUpdatingStatus(true);
    try {
      const token = getToken();
      const response = await axios.put(
        `${API_URL}/contact/requests/${selectedRequest.id}/status`,
        { status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        showToast("Status updated successfully", "success");
        setModalOpen(false);
        fetchRequests(); // Refresh the list
      }
    } catch (error) {
      console.error("Error updating status:", error);
      showToast("Failed to update status", "error");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleExportToExcel = () => {
    if (filteredRequests.length === 0) {
      showToast("No data to export", "error");
      return;
    }

    const exportData = filteredRequests.map((req, index) => ({
      "#": index + 1,
      "Request Type": req.requestType,
      "Full Name": req.fullName,
      Email: req.email,
      Designation: req.designation,
      Mobile: req.mobile,
      Organization: req.organizationName,
      City: req.city,
      Taluka: req.taluka,
      District: req.district,
      State: req.state,
      "Referral Agent Code": req.referralAgentCode || "-",
      "Referral Agent Name": req.referralAgentName || "-",
      Status: req.status,
      "Email Verified": req.emailVerified ? "Yes" : "No",
      "Created At": formatTableDateTime12H(req.createdAt),
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Registration Requests");
    XLSX.writeFile(wb, `Registration_Requests_${new Date().toISOString().split("T")[0]}.xlsx`);
    showToast("Exported to Excel successfully", "success");
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: "warning", text: "Pending" },
      contacted: { color: "success", text: "Contacted" },
      rejected: { color: "danger", text: "Rejected" },
    };
    const config = statusConfig[status] || { color: "secondary", text: status };
    return <Badge color={config.color}>{config.text}</Badge>;
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setFilterStatus("all");
    setDateFrom("");
    setDateTo("");
    setCurrentPage(1);
  };

  return (
    <div className="organization-management">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`custom-toast custom-toast-${toast.type}`}>
          {toast.message}
        </div>
      )}

      {/* Header Section */}
      <div className="header-section">
        <div>
          <h2 className="page-title">Registration Requests</h2>
          <p className="page-subtitle">
            Manage and review contact form submissions
          </p>
        </div>
        <Button color="success" onClick={handleExportToExcel}>
          <AiOutlineFileExcel /> Export to Excel
        </Button>
      </div>

      {/* Filters Section */}
      <Card className="filter-card">
        <CardBody>
          <Row>
            <Col md={3}>
              <FormGroup>
                <Label>Search</Label>
                <div className="search-input-wrapper">
                  <AiOutlineSearch className="search-icon" />
                  <Input
                    type="text"
                    placeholder="Search by name, email, org, referral..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </FormGroup>
            </Col>
            <Col md={2}>
              <FormGroup>
                <Label>Status</Label>
                <Input
                  type="select"
                  value={filterStatus}
                  onChange={(e) => {
                    setFilterStatus(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="contacted">Contacted</option>
                  <option value="rejected">Rejected</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md={2}>
              <FormGroup>
                <Label>From Date</Label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </FormGroup>
            </Col>
            <Col md={2}>
              <FormGroup>
                <Label>To Date</Label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </FormGroup>
            </Col>
            <Col md={3} className="d-flex align-items-end">
              <FormGroup className="w-100">
                <Button
                  color="secondary"
                  outline
                  onClick={handleResetFilters}
                  className="w-100"
                >
                  Reset Filters
                </Button>
              </FormGroup>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Stats Card */}
      <Card className="stats-card mb-3">
        <CardBody>
          <Row>
            <Col md={3}>
              <div className="stat-item">
                <span className="stat-value">{totalRecords}</span>
                <span className="stat-label">Total Requests</span>
              </div>
            </Col>
            <Col md={3}>
              <div className="stat-item">
                <span className="stat-value">
                  {requests.filter((r) => r.status === "pending").length}
                </span>
                <span className="stat-label">Pending</span>
              </div>
            </Col>
            <Col md={3}>
              <div className="stat-item">
                <span className="stat-value">
                  {requests.filter((r) => r.status === "contacted").length}
                </span>
                <span className="stat-label">Contacted</span>
              </div>
            </Col>
            <Col md={3}>
              <div className="stat-item">
                <span className="stat-value">
                  {filteredRequests.length}
                </span>
                <span className="stat-label">Filtered Results</span>
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Table Section */}
      <Card>
        <CardBody>
          {loading ? (
            <div className="text-center py-5">
              <Spinner />
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-muted">No registration requests found</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover className="organizations-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Request Type</th>
                      <th>Full Name</th>
                      <th>Email</th>
                      <th>Mobile</th>
                      <th>Organization</th>
                      <th>Address</th>
                      <th>Referral Code</th>
                      <th>Referral Agent</th>
                      <th>Status</th>
                      <th>Created At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRequests.map((request, index) => (
                      <tr key={request.id}>
                        <td>{(currentPage - 1) * requestsPerPage + index + 1}</td>
                        <td>
                          <Badge color="info">{request.requestType}</Badge>
                        </td>
                        <td>{request.fullName}</td>
                        <td>
                          {request.email}
                          {request.emailVerified && (
                            <AiOutlineCheckCircle
                              className="text-success ms-1"
                              title="Email Verified"
                            />
                          )}
                        </td>
                        <td>{request.mobile}</td>
                        <td>{request.organizationName}</td>
                        <td>
                          {request.city}, {request.district}, {request.state}
                        </td>
                        <td>
                          {request.referralAgentCode ? (
                            <Badge color="secondary">{request.referralAgentCode}</Badge>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>{request.referralAgentName || <span className="text-muted">-</span>}</td>
                        <td>{getStatusBadge(request.status)}</td>
                        <td>{formatTableDateTime12H(request.createdAt)}</td>
                        <td>
                          <Button
                            color="primary"
                            size="sm"
                            onClick={() => handleViewDetails(request)}
                            title="View Details"
                          >
                            <AiOutlineEye />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="pagination-container">
                <div className="pagination-info">
                  Showing {(currentPage - 1) * requestsPerPage + 1} to{" "}
                  {Math.min(currentPage * requestsPerPage, filteredRequests.length)} of{" "}
                  {totalRecords} entries
                </div>
                <div className="pagination-controls">
                  <Button
                    color="primary"
                    outline
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <span className="page-numbers">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <Button
                          key={pageNum}
                          color={currentPage === pageNum ? "primary" : "light"}
                          size="sm"
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </span>
                  <Button
                    color="primary"
                    outline
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardBody>
      </Card>

      {/* Details Modal */}
      {modalOpen && (
        <div className="credit-modal-overlay">
          <div className="credit-modal-content wide" onClick={e => e.stopPropagation()}>
            <div className="credit-modal-header">
              <h2>Request Details</h2>
              <button className="credit-modal-close" onClick={() => setModalOpen(false)}>×</button>
            </div>
            <div className="credit-modal-body">
              {selectedRequest && (
                <>
                  {/* Request Information */}
                  <h6 className="am-category-title">Request Information</h6>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label className="am-form-label">Request Type</Label>
                        <div style={{ paddingTop: '7px' }}>
                          <Badge color="info">{selectedRequest.requestType}</Badge>
                        </div>
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label className="am-form-label">Update Status</Label>
                        <Input
                          type="select"
                          value={newStatus}
                          onChange={(e) => setNewStatus(e.target.value)}
                        >
                          <option value="pending">Pending</option>
                          <option value="contacted">Contacted</option>
                          <option value="rejected">Rejected</option>
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>

                  {/* Personal Information */}
                  <h6 className="am-category-title">Personal Information</h6>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label className="am-form-label">Full Name</Label>
                        <Input value={selectedRequest.fullName || "-"} readOnly disabled />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label className="am-form-label">Designation</Label>
                        <Input value={selectedRequest.designation || "-"} readOnly disabled />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label className="am-form-label">Email</Label>
                        <div className="input-with-badge">
                          <Input value={selectedRequest.email || "-"} readOnly disabled />
                          {selectedRequest.emailVerified && (
                            <AiOutlineCheckCircle
                              className="verified-icon-inline"
                              title="Email Verified"
                            />
                          )}
                        </div>
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label className="am-form-label">Mobile</Label>
                        <Input value={selectedRequest.mobile || "-"} readOnly disabled />
                      </FormGroup>
                    </Col>
                  </Row>

                  {/* Organization Details */}
                  <h6 className="am-category-title">Organization Details</h6>
                  <Row>
                    <Col md="12">
                      <FormGroup>
                        <Label className="am-form-label">Organization/Bank/Company Name</Label>
                        <Input value={selectedRequest.organizationName || "-"} readOnly disabled />
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <Label className="am-form-label">City/Village</Label>
                        <Input value={selectedRequest.city || "-"} readOnly disabled />
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <Label className="am-form-label">Taluka</Label>
                        <Input value={selectedRequest.taluka || "-"} readOnly disabled />
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <Label className="am-form-label">District</Label>
                        <Input value={selectedRequest.district || "-"} readOnly disabled />
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <Label className="am-form-label">State</Label>
                        <Input value={selectedRequest.state || "-"} readOnly disabled />
                      </FormGroup>
                    </Col>
                  </Row>

                  {selectedRequest.message && (
                    <>
                      <h6 className="am-category-title">Additional Message</h6>
                      <FormGroup>
                        <Label className="am-form-label">Message</Label>
                        <Input
                          type="textarea"
                          value={selectedRequest.message}
                          readOnly
                          disabled
                          rows="4"
                        />
                      </FormGroup>
                    </>
                  )}

                  {/* Referral Information */}
                  <h6 className="am-category-title">Referral Information</h6>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label className="am-form-label">Referral Agent Code</Label>
                        <Input 
                          value={selectedRequest.referralAgentCode || "-"} 
                          readOnly 
                          disabled 
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label className="am-form-label">Referral Agent Name</Label>
                        <Input 
                          value={selectedRequest.referralAgentName || "-"} 
                          readOnly 
                          disabled 
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  {/* System Information */}
                  <h6 className="am-category-title">System Information</h6>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label className="am-form-label">Submitted At</Label>
                        <Input value={formatTableDateTime12H(selectedRequest.createdAt)} readOnly disabled />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label className="am-form-label">Email Verified</Label>
                        <div style={{ paddingTop: '7px' }}>
                          {selectedRequest.emailVerified ? (
                            <Badge color="success">
                              <AiOutlineCheckCircle /> Verified
                            </Badge>
                          ) : (
                            <Badge color="danger">
                              <AiOutlineCloseCircle /> Not Verified
                            </Badge>
                          )}
                        </div>
                      </FormGroup>
                    </Col>
                  </Row>
                </>
              )}
            </div>
            <div className="credit-modal-footer">
              <Button
                color="primary"
                onClick={handleStatusUpdate}
                disabled={updatingStatus || !selectedRequest || newStatus === selectedRequest.status}
              >
                {updatingStatus ? 'Updating...' : 'Update Status'}
              </Button>
              <Button color="secondary" onClick={() => setModalOpen(false)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

